import useEventCallback from '@unifyapps/hooks/useEventCallback';
import SvgChevronRight from '@unifyapps/icons/outline/ChevronRight';
import { Box } from '@unifyapps/ui/components/Box';
import { Divider } from '@unifyapps/ui/components/Divider';
import { IconButton } from '@unifyapps/ui/components/IconButton';
import { Stack } from '@unifyapps/ui/components/Stack';
import { Typography } from '@unifyapps/ui/components/Typography';
import { clsx } from 'clsx';
import {
  type Dispatch,
  memo,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCollapse } from 'react-collapsed';
import { EMPTY_OBJECT } from '../../../../consts/empty';
import { Link } from '../../../../navigation';
import { groupContainsActiveNavItem, hasSubnavigationItems } from '../../helpers';
import type {
  GroupedNavItem,
  NavApplicationItemType,
  NavItem,
  NavItemGroup,
  NavigationItemSlotPropsType,
} from '../../types';
import { getRedirectionProps, resolveOpenInNewTab } from '../../utils/redirection';
import {
  subItemChevronVariants,
  subItemLabelVariants,
  subItemRootVariants,
  subSubItemLabelVariants,
  subSubItemRootVariants,
} from './styles';

interface NavSubItemProps {
  item: NavItemGroup | NavItem | NavApplicationItemType;
  slotProps?: NavigationItemSlotPropsType;
  activeNavItem?: NavItem | NavApplicationItemType;
  activeSubGroupId?: string | null;
  setActiveSubGroupId?: Dispatch<SetStateAction<string | null>>;
}

const NestedSubNavLink = memo(function NestedSubNavLink({
  subItem,
  className,
  children,
}: {
  subItem: NavItem | NavApplicationItemType;
  className: string;
  children: ReactNode;
}) {
  const openInNewTabResolved = resolveOpenInNewTab(subItem);
  const redirectionProps = useMemo(
    () => getRedirectionProps(openInNewTabResolved),
    [openInNewTabResolved],
  );

  return (
    <Link className={className} href={subItem.url} {...redirectionProps}>
      {children}
    </Link>
  );
});

const LinkWrapper = (props: {
  children: React.ReactNode;
  item: NavSubItemProps['item'];
  className: string;
}) => {
  const { children, item, className } = props;

  const openInNewTabResolved =
    item.type !== 'GROUP'
      ? resolveOpenInNewTab(item as NavItem | NavApplicationItemType)
      : undefined;
  const redirectionProps = useMemo(
    () => getRedirectionProps(openInNewTabResolved),
    [openInNewTabResolved],
  );

  if (item.type === 'GROUP') return <>{children}</>;

  const { url } = item as NavItem | NavApplicationItemType;

  return (
    <Link className={className} href={url} {...redirectionProps}>
      {children}
    </Link>
  );
};

function NavigationSubItem(props: NavSubItemProps) {
  const { item, slotProps, activeNavItem, activeSubGroupId, setActiveSubGroupId } = props;

  const { expanded: isSideNavExpanded = true } =
    slotProps?.navigationItem ?? (EMPTY_OBJECT as NavigationItemSlotPropsType['navigationItem']);

  const isItemActive = useMemo(() => {
    if (item.type !== 'GROUP') return item.id === activeNavItem?.id;
    // recurse so a group highlights when any descendant (not only a direct child) is active,
    // matching the collapsed-rail flyout's selection model across n levels.
    return groupContainsActiveNavItem(item, activeNavItem?.id);
  }, [item, activeNavItem]);

  const isItemExpanded = item.id === activeSubGroupId;
  const hasSubItems = hasSubnavigationItems(item);

  // one-open-per-level accordion for THIS group's child groups. The item's own open/closed state is
  // driven by the parent via activeSubGroupId; this local state drives the next level down, letting
  // the rail nest to n levels the same way the flyout's FlyoutItems does.
  const [activeChildGroupId, setActiveChildGroupId] = useState<string | null>(null);

  // generalises NavigationGroup's L1->L2 reset to every level: when this group collapses, close
  // whichever child group it had open so re-expanding starts clean.
  useEffect(() => {
    if (!isItemExpanded && activeChildGroupId !== null) {
      setActiveChildGroupId(null);
    }
  }, [isItemExpanded, activeChildGroupId]);

  const subItemRootClassName = subItemRootVariants({
    active: isItemActive,
    expanded: isItemExpanded,
    className: 'relative ua-platform-side-nav-nested-item group/PlatformSideNavNestedItem',
  });

  const subItemLabelClassName = subItemLabelVariants({
    active: isItemActive,
    isSideNavExpanded,
  });

  // rotation is handed to the glyph (iconClassName below), not the button: the button's end padding
  // is zeroed (asymmetric box), so rotating the button would carry the missing inset to its bottom.
  const subItemChevronClassName = subItemChevronVariants({
    expanded: false,
    active: isItemActive,
  });

  const subSubItemLabelClassName = ({ active }: { active: boolean }) =>
    subSubItemLabelVariants({
      active,
      isSideNavExpanded,
    });

  const subSubItemRootClassName = ({ active }: { active: boolean }) =>
    subSubItemRootVariants({
      active,
      className: ' relative ua-platform-side-nav-nested2-item',
    });

  const { getCollapseProps, getToggleProps } = useCollapse({
    isExpanded: isItemExpanded,
    easing: 'ease-in-out',
    duration: 150,
  });

  const onClick = useEventCallback(() => {
    if (item.type !== 'GROUP') return;

    const updatedValue = isItemExpanded ? null : item.id;
    setActiveSubGroupId?.(updatedValue);
  });

  return (
    <Stack className="gap-y-xs" direction="column" key={item.id}>
      <LinkWrapper className={subItemRootClassName} item={item}>
        <Stack
          className={item.type !== 'GROUP' ? '' : subItemRootClassName}
          component={item.type !== 'GROUP' ? 'div' : 'button'}
          direction="row"
          justifyContent="space-between"
          {...(item.type !== 'GROUP' ? {} : { ...getToggleProps() })}
          onClick={onClick}
        >
          <Typography
            as="span"
            className={subItemLabelClassName}
            maxNumberOfLines={1}
            variant="text-sm"
            weight="regular"
          >
            {item.name}
          </Typography>
          {hasSubItems ? (
            <IconButton
              aria-hidden
              className={`${subItemChevronClassName} ua-platform-side-nav-chevron-button !size-5 !rounded-xs !pe-none`}
              color="neutral"
              component="span"
              Icon={SvgChevronRight}
              iconClassName={clsx(
                '!size-3 transition-transform',
                isItemExpanded && 'rotate-90',
                isItemActive
                  ? 'ua-side-nav-nested-item-active'
                  : 'ua-side-nav-nested-item-active-false',
              )}
              size="sm"
              variant="ghost"
            />
          ) : null}
        </Stack>
      </LinkWrapper>
      <Box {...getCollapseProps()}>
        <Stack className="gap-x-sm ps-md" direction="row">
          <Divider className="border-primary" orientation="vertical" />

          {isSideNavExpanded && hasSubItems && (
            <Stack className="gap-y-xs w-full" direction="column">
              {'items' in item &&
                item.items?.map((childItem: GroupedNavItem) => {
                  // a nested group recurses back through NavigationSubItem so it renders its own
                  // header + collapse region — this is what lifts the rail from a fixed 3 levels to
                  // n levels, mirroring the flyout's FlyoutItems recursion. Its open child is tracked
                  // by this level's own activeChildGroupId (one-open-per-level accordion).
                  if (childItem.type === 'GROUP') {
                    return (
                      <NavigationSubItem
                        activeNavItem={activeNavItem}
                        activeSubGroupId={activeChildGroupId}
                        item={childItem}
                        key={childItem.id}
                        setActiveSubGroupId={setActiveChildGroupId}
                        slotProps={slotProps}
                      />
                    );
                  }

                  const isActive = childItem.id === activeNavItem?.id;

                  return (
                    <NestedSubNavLink
                      className={subSubItemRootClassName({ active: isActive })}
                      key={childItem.id}
                      subItem={childItem}
                    >
                      <Box className="w-full rounded-sm py-xs ps-sm">
                        <Typography
                          as="span"
                          className={subSubItemLabelClassName({ active: isActive })}
                          maxNumberOfLines={1}
                          variant="text-sm"
                          weight="regular"
                        >
                          {childItem.name}
                        </Typography>
                      </Box>
                    </NestedSubNavLink>
                  );
                })}
            </Stack>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

export default memo(NavigationSubItem);
